import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getChases, waiveChase, reopenChase, money } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FileSearch, Check, Ban, RotateCcw, ArrowUpRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const TONE = { open: "text-amber-500 bg-amber-500/15", received: "text-emerald-500 bg-emerald-500/15", waived: "text-muted-foreground bg-muted" };

export const ChasesCard = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => getChases(status).then(setData), [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (window.location.hash === "#chases") setTimeout(() => document.getElementById("chases")?.scrollIntoView({ behavior: "smooth" }), 200);
  }, []);

  const act = async (c, fn, msg) => {
    setBusy(c.id);
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setBusy(null);
  };

  return (
    <div id="chases" className="rounded-xl border border-border bg-card overflow-hidden" data-testid="chases-card">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 text-sm font-medium">
          <FileSearch className="h-4 w-4 text-amber-500" /> Missing invoice chases
          {data && <span className="rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-mono text-amber-500" data-testid="chases-open-count">{data.open_total} open</span>}
          {data && status === "open" && data.open_value > 0 && <span className="text-xs text-muted-foreground font-mono">{money(data.open_value, data.items[0]?.currency)}</span>}
        </div>
        <div className="flex rounded-lg border border-border p-0.5 text-xs">
          {["open", "received", "waived", "all"].map((k) => (
            <button key={k} data-testid={`chases-filter-${k}`} onClick={() => setStatus(k)} className={cn("rounded-md px-2.5 py-1 font-medium capitalize", status === k ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground")}>{k}</button>
          ))}
        </div>
      </div>
      {!data ? (
        <div className="px-4 py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
      ) : data.items.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="chases-empty">
          {status === "open" ? "Nothing to chase — every statement line has a matching invoice." : `No ${status === "all" ? "" : status} chases.`}
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {data.items.map((c) => (
            <li key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-2.5" data-testid={`chase-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.supplier} <span className="font-mono text-xs text-muted-foreground">{c.invoice_number || "(no reference)"}</span>
                  <span className={cn("ml-2 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase", TONE[c.status])} data-testid="chase-status">{c.status}</span></div>
                <div className="text-xs text-muted-foreground truncate">{c.line_date} · {c.line_type}{c.description ? ` · ${c.description}` : ""} · opened {new Date(c.created_at).toLocaleDateString()}{c.closed_at ? ` · closed ${new Date(c.closed_at).toLocaleDateString()}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-sm">{money(c.amount, c.currency)}</span>
                {c.status === "open" && (
                  <Button data-testid="chase-waive-btn" size="sm" variant="outline" disabled={busy === c.id} onClick={() => act(c, () => waiveChase(c.id, user), `Waived ${c.invoice_number}`)}>
                    <Ban className="h-3.5 w-3.5 mr-1" /> Waive
                  </Button>
                )}
                {c.status === "received" && c.invoice_id && (
                  <Button data-testid="chase-open-invoice" size="sm" variant="ghost" onClick={() => navigate(`/invoice/${c.invoice_id}`)}><Check className="h-3.5 w-3.5 mr-1 text-emerald-500" /> Invoice <ArrowUpRight className="h-3 w-3 ml-1" /></Button>
                )}
                {c.status !== "open" && (
                  <Button data-testid="chase-reopen-btn" size="sm" variant="ghost" disabled={busy === c.id} onClick={() => act(c, () => reopenChase(c.id, user), `Re-opened ${c.invoice_number}`)}><RotateCcw className="h-3.5 w-3.5" /></Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
